/**
 * O pedido de ajuste de Terraform, do lado do cartão do chat (D1/D11).
 *
 * `GET /api/v1/terraform/change-requests/{id}` — `terraform_runner/routes.py`,
 * `cartao_do_pedido.resumo_para_o_cartao` (backend, Task 8). Só leitura, e só
 * o que a pessoa decide com: nunca `before`/`after` do plan (valor sensível).
 */
import { api } from '@/api/client'
import type { ChecagemLaudo } from '@/pages/chat/components/ChecagensDoLaudo'

export interface EtapaDoPedido {
  estado: string
  rotulo: string
  status: 'feita' | 'em_curso' | 'pendente' | 'terminal'
  em: string | null
}

export interface EmCursoDoPedido {
  estado: string
  frase: string
  quem: string
  /** Mediana medida nos últimos 20 pedidos; `null` com menos de 3 medições. */
  estimativa_min: number | null
  /** Até quando o estado espera uma pessoa antes de vencer (confirmar, merge
   * do MR, GMUD); `null` quando quem se espera é um sistema. */
  prazo_ate?: string | null
  /** A vez é de uma pessoa (Confirmar, aprovação, merge, GMUD): nada executa. */
  espera_pessoa?: boolean
}

/** As três linhas de um pedido que parou (`linha_do_tempo.desfecho`). */
export interface DesfechoDoPedido {
  o_que_aconteceu: string
  codigo_e_aws: string
  proximo_passo: string
}

export interface PedidoDoCartao {
  id: number
  estado: string
  stage_key: string
  terminal: boolean
  motivo_terminal: string | null
  linha_plan: string | null
  acoes: Record<string, string[]>
  resumo: Record<string, unknown>
  inesperadas: string[]
  /** Na retomada da criação, os creates fora do plano aprovado (spec
   * 2026-10-01-retomada-aceita-criacao-nova D3). Ausente em servidor antigo. */
  novos_desde_o_aprovado?: string[]
  /** consoleai#26352: registros DNS do plan que tiram do ZTNA um host publicado.
   * Ausente em servidor antigo. */
  encobre_ztna?: EncobreZtna[]
  mr_url: string | null
  pode_confirmar: boolean
  pode_recusar: boolean
  /** D8, aceite 4: flag do merge por identidade ligada e quem vê sem vínculo
   * GitLab — o Confirmar dá lugar a "Conectar GitLab" (`pode_confirmar` já
   * vem falso do servidor). */
  precisa_vincular_gitlab: boolean
  linha_do_tempo: {
    etapas: EtapaDoPedido[]
    em_curso: EmCursoDoPedido | null
    desfecho?: DesfechoDoPedido | null
  }
  /** D13: em execução, ou nos 10 min depois do fim dela — decide se o
   * painel "Ver execução" aparece no cartão (`leitor_do_andamento.andamento_disponivel`). */
  andamento_disponivel: boolean
  /** O chamado do pedido (spec 2026-10-01 D2); `null` no pedido sem chamado. */
  chamado_id?: number | null
  /** Só em `aguardando_aprovacao`: o eixo que falta, quem pode decidi-lo e se
   * QUEM VÊ pode (`pode_aprovar_pedido` e não ser quem pediu). */
  aprovacao?: AprovacaoDoPedido | null
  /** A criação que parou em `apply_parcial` pode ser completada por quem vê
   * (TASK-12154): a regra do service e os portões de criar ambiente. */
  pode_completar?: boolean
  /** O pedido que ESTE completa, quando ele é uma retomada da criação. */
  completa_pedido_id?: number | null
  /** O pedido que o botão "Completar a criação" completa — a origem, quando o mostrado é uma retomada que morreu antes do apply. */
  completar_pedido_id?: number | null
  /** O alvo do botão é uma criação sem rede: o botão é "Refazer a rede" (TASK-12167). */
  completar_refaz_rede?: boolean
  /** O alvo do botão é um ajuste livre em `apply_parcial`: o botão é
   * "Reaplicar a partir da master" (consoleai#26379). */
  completar_reaplica?: boolean
  /** Spec 2026-10-15 §5.8: o lugar na fila, calculado na leitura; só em `na_fila`. */
  posicao?: number | null
  /** Spec 2026-10-15 D3: quem vê é quem pediu, e o pedido está guardado em
   * `aguardando_pedinte` — o botão "Quero seguir" aparece. */
  pode_retomar?: boolean
  /** Guardado porque o código do stage mudou nos arquivos do ajuste: o "Quero
   * seguir" não serve (`pode_retomar` vem falso) — pedir de novo no chat. */
  base_mudou?: boolean
  /** Quem vê é quem pediu — decide o selo "Esperando você", separado do botão
   * (review da Onda 3, M2). Ausente em servidor antigo. */
  sou_o_pedinte?: boolean
  /** Adendo I2 (spec 2026-10-15 §5.4): só no pedido que pausou a fila do stage,
   * com a pausa aberta. `pode_retomar` é o eixo da rota `retomar-fila`. */
  pausa?: PausaDoStage | null
  /** Spec 2026-11-03 D7: a janela de construção do stage, só no ajuste livre de
   * stage com janela registrada. Ausente em servidor antigo. */
  janela?: JanelaDoPedido | null
}

export interface JanelaDoPedido {
  aberta: boolean
  /** `aberta`, `fechada` ou `expirada`. */
  estado: string
  expira_em: string | null
  /** O pedido seguiu (ou segue) sem Sênior pela janela. */
  dispensou: boolean
  /** Por que não dispensou (`superficie_sensivel`, `plano_com_destrutivas`, …). */
  motivo: string
  sensiveis: string[]
  /** Stage criado pelo runner, fora de produção: o Sênior pode reabrir. */
  pode_reabrir?: boolean
  tenant_id: number
}

export interface JanelaDoStage {
  tenant_id: number
  chave: string
  origem: string | null
  producao: boolean
  estado: string
  aberta_em: string | null
  expira_em: string | null
  fechada_em: string | null
  fechada_por: number | null
  pode_reabrir: boolean
}

export const fecharJanelaDoStage = (tenantId: number): Promise<JanelaDoStage> =>
  api.post(`/v1/terraform/stage-bindings/${tenantId}/janela/fechar`).then((r) => r.data)

export const reabrirJanelaDoStage = (tenantId: number, motivo: string): Promise<JanelaDoStage> =>
  api.post(`/v1/terraform/stage-bindings/${tenantId}/janela/reabrir`, { motivo }).then((r) => r.data)

export interface PausaDoStage {
  id: number
  stage_key: string
  motivo: string
  /** O chamado de saneamento; `null` até o worker abri-lo. */
  task_request_id: number | null
  pode_retomar: boolean
}

export interface AprovacaoDoPedido {
  eixo_pendente: 'devops' | 'gestor' | string
  pode_aprovar: boolean
  /** Quem lê é o solicitante e aprova como Administrador (TASK-12153): a
   *  aprovação exige justificativa e não há Recusar. */
  autoaprovacao?: boolean
  aprovadores: { user_id: number; nome: string }[]
  /** Ajuste livre (spec 2026-10-08 D15): cada endereço destruído ou exposto
   *  que o eixo devops confirma um a um antes de aprovar. */
  enderecos_a_confirmar?: string[]
  /** Spec 2026-10-15 WAF 100% D6: cada ponto em que o plano deixa o WAF mais
   *  fraco (IP na AllowList, regra de block para count), por endereço. */
  afrouxa?: { endereco: string; o_que: string }[]
  /** Os atributos que forçam a recriação de cada endereço destrutivo. */
  replace_paths?: Record<string, (string | number)[][]>
  /** consoleai#26352: os registros DNS que tiram host do ZTNA, com o app id;
   *  aprovar pede o motivo escrito (`motivo_minimo`). */
  encobre_ztna?: EncobreZtna[]
  /** Mínimo de caracteres do motivo da aprovação; 0 quando não se exige. */
  motivo_minimo?: number
  /** Com o merge pelo aprovador ligado, quem vê ainda não conectou o GitLab:
   *  o botão é "Conectar GitLab" em vez de "Aprovar". */
  precisa_gitlab?: boolean
  /** consoleai#26173: pedidos anteriores do stage que tocaram os mesmos
   *  endereços deste plano e NÃO aplicaram (cancelados, recusados, plan que
   *  falhou), com o motivo gravado. O mais recente primeiro. */
  anteriores_no_mesmo_endereco?: PedidoAnteriorNoEndereco[]
}

export interface PedidoAnteriorNoEndereco {
  id: number
  estado: string
  motivo: string | null
  pedido: string
  em_comum: string[]
  quando: string | null
}

/** Um registro DNS do plan que tira do ZTNA o host publicado (consoleai#26352).
 *  `app_id` nulo: não deu para ler os hosts do ZTNA, e o registro conta. */
export interface EncobreZtna {
  endereco: string
  host: string
  app_id: number | null
  curinga: string | null
}

/** Completa a criação parada (TASK-12154): cria o pedido novo, que planeja a
 * partir da main só o que faltou e passa pela aprovação antes do apply. */
export const completarCriacao = (
  id: number,
): Promise<{ pedido_id: number; completa_pedido_id: number }> =>
  api.post(`/v1/terraform/change-requests/${id}/completar-criacao`).then((r) => r.data)

/** Spec 2026-10-15 D3: o "Quero seguir" do ajuste livre guardado. O pedido volta
 * à fila como o próximo; 403 para quem não pediu, 409 fora de `aguardando_pedinte`. */
export const retomarPedido = (
  id: number,
): Promise<{ pedido_id: number; estado: string | null; posicao: number | null }> =>
  api.post(`/v1/terraform/change-requests/${id}/retomar`).then((r) => r.data)

/** Adendo I2: o DevOps saneou o stage e retoma a fila. 403 sem o eixo devops
 * (ou de sessão de celular), 409 sem fila parada. */
export const retomarFilaDoStage = (
  stageKey: string,
): Promise<{ stage_key: string; pausa_id: number; pedido_que_sujou: number; retomada: boolean }> =>
  api.post(`/v1/terraform/stages/${stageKey.split('/').map(encodeURIComponent).join('/')}/retomar-fila`)
    .then((r) => r.data)

export const getPedidoTerraform = (id: number): Promise<PedidoDoCartao> =>
  api.get(`/v1/terraform/change-requests/${id}`).then((r) => r.data)

/** O pedido que o chamado mostra (D1): o mais recente não terminal, senão o
 * mais recente. 404 quando o chamado não tem pedido ou quem vê não o lê. */
export const getPedidoDoChamado = (taskRequestId: number): Promise<PedidoDoCartao> =>
  api.get(`/v1/terraform/change-requests/do-chamado/${taskRequestId}`).then((r) => r.data)

/** O pedido de criação do rascunho de `terraform_stage_create`: o cartão do
 * chat só conhece o `rascunho_id`. Mesmo corpo e mesmo acesso da rota por id;
 * 404 quando o rascunho ainda não tem pedido ou quem vê não o lê. */
export const getPedidoDoRascunho = (rascunhoId: number): Promise<PedidoDoCartao> =>
  api.get(`/v1/terraform/change-requests/do-rascunho/${rascunhoId}`).then((r) => r.data)

/** D3: aprovar ou recusar o eixo corrente. Recusa sem `motivo` volta 422
 * `justificativa_obrigatoria` — o painel não deixa enviar antes disso. */
export const decidirPedido = (
  id: number,
  corpo: { decisao: 'aprovado' | 'recusado'; motivo?: string; enderecos_confirmados?: string[] },
): Promise<{ pedido_id: number; estado: string }> =>
  api.post(`/v1/terraform/change-requests/${id}/aprovar`, corpo).then((r) => r.data)

/**
 * D13 — `GET /v1/terraform/change-requests/{id}/andamento` (Task 15,
 * `terraform_runner/leitor_do_andamento.py`). `proximo` é o token opaco da
 * leitura seguinte (`depois=`); o cartão repassa sem interpretar. `stream`
 * identifica o log stream lido: o painel zera as linhas quando ele muda OU
 * quando `reiniciado` vem `true` — nunca pelo rótulo `execucao`, que pode
 * repetir entre disparos (replan usa a mesma família `tf-apply` do apply).
 * `situacao` inclui `atrasado` (erro transitório da AWS, o cartão mantém o
 * que já mostrou) e `indisponivel` (erro persistente, ex.: policy do
 * CloudWatch ainda não aplicada) — nenhum dos dois é erro fatal: o servidor
 * nunca devolve 500 aqui (review I1), e o polling continua.
 */
export interface AndamentoDoPedido {
  disponivel: boolean
  execucao: string | null
  stream: string | null
  linhas: string[]
  proximo: string | null
  reiniciado: boolean
  situacao: 'iniciando' | 'ok' | 'atrasado' | 'indisponivel'
  lido_em: string
}

/** `viaChamado`: o painel aberto no chamado (TASK-12155) — ler o chamado vale
 * no lugar da conversa, como na rota `do-chamado`. */
export const getAndamentoTerraform = (
  id: number, depois: string | null, viaChamado?: number | null,
): Promise<AndamentoDoPedido> => {
  const params: Record<string, string | number> = {}
  if (depois) params.depois = depois
  if (viaChamado != null) params.via_chamado = viaChamado
  return api.get(`/v1/terraform/change-requests/${id}/andamento`, {
    params: Object.keys(params).length ? params : undefined,
  }).then((r) => r.data)
}

/**
 * O painel do @DevOps (spec 2026-11-04-painel-do-devops D-4/D-5/D-9, §3.1):
 * `GET /v1/terraform/change-requests/{id}/painel-do-stage`. O stage é o do
 * pedido âncora, resolvido no servidor — o cliente nunca manda o stage. 404
 * uniforme para quem não lê a âncora.
 *
 * Só leitura: o corpo de cada pedido sai por LISTA DE PERMISSÃO no servidor e
 * não traz chave `pode_*` nenhuma. O painel não deduz ação de nada daqui.
 * Qualquer bloco pode vir `null`/`[]`: a leitura que falhou no servidor esconde
 * o bloco.
 */
export interface CorpoDoPainel {
  visivel: true
  id: number
  estado: string
  stage_key: string
  terminal: boolean
  motivo_terminal: string | null
  linha_plan: string | null
  acoes: Record<string, string[]>
  resumo: Record<string, unknown>
  inesperadas: string[]
  mr_url: string | null
  linha_do_tempo: PedidoDoCartao['linha_do_tempo']
  andamento_disponivel: boolean
  posicao: number | null
  sou_o_pedinte: boolean
  aprovacao: { eixo_pendente: string; aprovadores: { user_id: number; nome: string }[] } | null
  /** Ajuste livre com patch: o painel oferece "Diff do arquivo" (§3.3). */
  tem_diff: boolean
  /** O que foi conferido e o que não foi verificado (§3.4), no formato do laudo.
   *  `null` quando nada foi registrado. */
  conferencia: { checagens: ChecagemLaudo[] } | null
  /** Spec 2026-11-03 D7: o marco da janela de construção, sem ação. Ausente em servidor antigo. */
  janela?: Pick<JanelaDoPedido, 'aberta' | 'estado' | 'expira_em' | 'dispensou' | 'motivo' | 'sensiveis'> | null
}

/** D-9: o pedido que quem vê não lê — sem id, estado, autor nem texto. */
export interface PedidoOpaco {
  visivel: false
}

export interface PedidoOpacoNaFila extends PedidoOpaco {
  posicao: number
}

/** A pausa do stage. Sem ler o pedido que a causou, sai só a classe. */
export interface PausaDoPainel {
  classe: string
  motivo?: string
  pedido_id?: number
  chamado_id?: number | null
  pausada_em?: string | null
}

/** Um item da lista que a pessoa pediu num turno (§3.6, consoleai#26337). O `trecho` é o
 *  texto literal dela; o `rotulo` (do estado do pedido) e o `motivo` vêm do servidor. */
export interface ItemDaLista {
  n: number
  ref: string
  trecho: string
  /** `na_fila`: espera o "segue" na fila da conversa, ainda sem pedido. */
  situacao: 'pedido' | 'na_fila' | 'em_curso' | 'nao_saiu'
  pedido_id: number | null
  estado: string | null
  rotulo: string | null
  motivo: string | null
  /** O item que não saiu volta à fila. */
  de_novo: boolean
}

export interface PedidoRecente {
  id: number
  estado: string
  stage_key: string
  motivo_terminal: string | null
  desfecho: DesfechoDoPedido | null
}

export interface PainelDoStage {
  stage_key: string
  /** `null` quando o servidor não devolve o pedido âncora: o painel mostra o aviso de
   * leitura e não relê. */
  ancora: CorpoDoPainel | null
  em_curso: CorpoDoPainel | PedidoOpaco | null
  fila: (CorpoDoPainel | PedidoOpacoNaFila)[] | null
  pausa: PausaDoPainel | null
  recentes: PedidoRecente[] | null
  itens_da_lista: ItemDaLista[] | null
}

export const getPainelDoStage = (
  pedidoId: number, viaChamado?: number | null,
): Promise<PainelDoStage> =>
  api.get(`/v1/terraform/change-requests/${pedidoId}/painel-do-stage`, {
    params: viaChamado != null ? { via_chamado: viaChamado } : undefined,
  }).then((r) => r.data)

/** Um arquivo do ajuste livre: o diff unificado já sai mascarado do servidor, e `cortado`
 *  diz que ele parou no teto de linhas. */
export interface ArquivoDoDiff {
  arquivo: string
  novo: boolean
  diff: string
  cortado: boolean
}

/** Sem diff (HCL ilegível, GitLab fora): só o `motivo`, nunca o texto cru. */
export type DiffDoPedido =
  | { disponivel: true; arquivos: ArquivoDoDiff[] }
  | { disponivel: false; motivo: string }

/** §3.3: `GET /v1/terraform/change-requests/{id}/diff`. Mesmo acesso e mesmo 404 uniforme
 *  do painel. */
export const getDiffDoPedido = (
  pedidoId: number, viaChamado?: number | null,
): Promise<DiffDoPedido> =>
  api.get(`/v1/terraform/change-requests/${pedidoId}/diff`, {
    params: viaChamado != null ? { via_chamado: viaChamado } : undefined,
  }).then((r) => r.data)
